import { IPaginated } from "@edgetier/types";

import {
    AdditionalGroupBy,
    AnalysisTimePeriod,
    IBackendModel,
    IItem,
    InteractionMetric,
    InteractionType,
    TimePeriodGroupBy,
} from "../common";
import { AgentStateType } from "./agent-states";
import { IAgent } from "./agents";
import { MessageDirection, WritingIssueType } from "./messages";
import { ITag } from "./tags";
import type { ScorecardEvaluationStatus } from "./scorecards";

interface IDynamicKeyField<T = any> {
    readonly [index: string]: T;
}

export interface IInteractionMetrics extends IDynamicKeyField {
    readonly agentMessagesCount: number | null;
    readonly agentFirstResponseTimeSeconds: number | null;
    readonly agentSatisfaction: number | null;
    readonly agentScore: number | null;
    readonly botMessagesCount: number | null;
    readonly customerMessagesCount: number | null;
    readonly customerSatisfaction: number | null;
    readonly queueTimeSeconds: number | null;
    readonly handlingTimeSeconds: number | null;
    readonly experienceScore: number | null;
    readonly messagesCount: number | null;
    readonly netPromoterScore: number | null;
    readonly firstResponseTimeSeconds: number | null;
    readonly isQueryResolved: boolean | null;
    readonly serviceLevelAgreementPercentage: number | null;
}

export type INumericInteractionMetric = Exclude<
    InteractionMetric,
    InteractionMetric.QueryResolution | InteractionMetric.ReviewsCoverage
>;

// Minimum and maximum query parameters for every metric.
export type IInteractionMaximumMetricParameters = {
    [Property in INumericInteractionMetric as `maximum${Capitalize<string & Property>}`]?: number;
};

export type IInteractionMinimumMetricParameters = {
    [Property in INumericInteractionMetric as `minimum${Capitalize<string & Property>}`]?: number;
};

export enum InteractionState {
    Blocked = "BLOCKED",
    Complete = "COMPLETE",
    Deflecting = "DEFLECTING",
    Handling = "HANDLING",
    Queuing = "QUEUING",
    WrapUp = "WRAP_UP",
}
export enum TextSearchType {
    Simple = "simple",
    Vector = "vector",
    OpenSearch = "open_search",
}

export enum InteractionSpotlightGroupSummaryTabs {
    ContactReasons = "contact-reasons",
    AgentReview = "agent-review",
}
export enum AgentSpotlightReviewSummaryTabs {
    AgentStrengths = "agent-strengths",
    AgentOpportunities = "agent-opportunities",
}
export type IInteractionsGetQueryParameters = {
    readonly analysisTimePeriod?: AnalysisTimePeriod;
    readonly agentExperienceRating?: ExperienceDriverRating | ExperienceDriverRating[];
    readonly agentId?: string | string[];
    readonly anomalyId?: string;
    readonly contactId?: string | string[];
    readonly agentStateType?: AgentStateType[];
    readonly agentTagGroupId?: string;
    readonly agentTagId?: string[];
    readonly botExperienceRating?: ExperienceDriverRating | ExperienceDriverRating[];
    readonly endDateTime?: Date;
    readonly includeMultiAgentConversations?: boolean;
    readonly groupBy?: TimePeriodGroupBy | TimePeriodGroupBy[] | string | string[];
    readonly hasFrustration?: boolean;
    readonly hasGratitude?: boolean;
    readonly hasPraise?: boolean;
    readonly hasConfusion?: boolean;
    readonly hasEmpathy?: boolean;
    readonly highlightMaximumWords?: number;
    readonly highlightMinimumWords?: number;
    readonly interactionState?: InteractionState[];
    readonly interactionType?: InteractionType[];
    readonly isAgentSearch?: boolean;
    readonly isBotSearch?: boolean;
    readonly isCustomerSearch?: boolean;
    readonly isMessageSearch?: boolean;
    readonly isMetadataSearch?: boolean;
    readonly isQueryResolved?: boolean;
    readonly issue?: string;
    readonly isSystemSearch?: boolean;
    readonly languageIsoCode?: string | string[];
    readonly limit?: number;
    readonly messageDirections?: MessageDirection[];
    readonly orderBy?: string;
    readonly parameterGroupId?: string;
    readonly productExperienceRating?: ExperienceDriverRating | ExperienceDriverRating[];
    readonly scorecardId?: string;
    readonly search?: string;
    readonly searchType?: string;
    readonly similarityThreshold?: number;
    readonly startDateTime?: Date;
    readonly tagGroupId?: string;
    readonly tagId?: string[];
    readonly textSearchType?: TextSearchType;
    readonly timeZone?: string;
    readonly webSearchSyntax?: boolean;
    readonly writingIssueType?: WritingIssueType;
    readonly beforeInteractionId?: string;
    readonly afterInteractionId?: string;
    readonly relativeDateTime?: string;
    readonly resolutionStatus?: ResolutionStatus | ResolutionStatus[];
    readonly reviewStatus?: ReviewStatus;
    readonly reviewerId?: string | string[];
    readonly scorecardEvaluationsCoverage?: number;
    readonly scorecardEvaluationState?: ScorecardEvaluationStatus | ScorecardEvaluationStatus[];
} & IInteractionMaximumMetricParameters &
    IInteractionMinimumMetricParameters;

export interface IInteractionGroupSummaryGetQueryParameters extends IInteractionsGetQueryParameters {
    readonly summaryType?: Omit<InteractionSpotlightGroupSummaryTabs, "AgentReview"> | AgentSpotlightReviewSummaryTabs;
}
export interface IInteractionAudioResponse {
    readonly url: string | null;
}
/**
 * A single agent that participated in an interaction, as returned in the `agents` list of the
 * interactions endpoints (WatchTower-API serializer, KRP-1264). `name` is resolved by the
 * backend from the agents join (it is not a column on the `interaction_agents` table);
 * `isFinalAgent` flags the final agent on the interaction.
 */
export interface IInteractionAgent extends Pick<IAgent, "agentId" | "name"> {
    readonly isFinalAgent: boolean;
    readonly isBot: boolean;
}

/** The AI-generated experience-driver rating per dimension. Null or absent when the dimension was not rated. */
export interface IInteractionExperienceRatings {
    readonly agentExperienceRating?: ExperienceDriverRating | null;
    readonly botExperienceRating?: ExperienceDriverRating | null;
    readonly productExperienceRating?: ExperienceDriverRating | null;
}

export interface IInteractionDetailsResponse extends IInteractionExperienceRatings {
    readonly agentId: string | null;
    readonly agents?: IInteractionAgent[];
    readonly blockedDateTime: Date | null;
    readonly clientId: string;
    readonly contactId: string | null;
    readonly contentCleaned: string;
    readonly contentHtml: string;
    readonly contentPlain: string;
    readonly endDateTime: Date;
    readonly externalReference: string;
    readonly enterQueueDateTime: Date | null;
    readonly exitQueueDateTime: Date | null;
    readonly handleDateTime: Date | null;
    readonly interactionId: string;
    readonly interactionType: InteractionType;
    readonly languageIsoCode: string;
    readonly resolutionRationale?: string | null;
    readonly resolutionStatus?: ResolutionStatus | null;
    readonly senderEmail: string | null;
    readonly senderName: string | null;
    readonly senderReference: string;
    readonly startDateTime: Date;
    readonly subject: string;
    readonly surveyedInteractionId: string | null;
    readonly tags: ITag[];
    readonly url: string;
}

type IInteractionsMaximumMetricParameters = {
    [Property in IInteractionMinimumMetricParameters as `interaction${Capitalize<string & Property>}`]?: number;
};

type IInteractionsMinimumMetricParameters = {
    [Property in IInteractionMaximumMetricParameters as `interaction${Capitalize<string & Property>}`]?: number;
};

export type IInteractionMessagesGetQueryParameters = {
    readonly agentId?: string | string[];
    readonly groupBy?: TimePeriodGroupBy | AdditionalGroupBy[];
    readonly limit?: number;
    readonly isMessageSearch?: boolean;
    readonly interactionType?: string[];
    readonly timeZone?: string;
    readonly webSearchSyntax?: boolean;
    readonly tagGroupId?: string;
    readonly issue?: string;
    readonly writingIssueType?: WritingIssueType;
    readonly contactId?: string | string[];
    readonly messageDirection?: MessageDirection;
    readonly startDateTime?: Date;
    readonly endDateTime?: Date;
    readonly interactionContactId?: string | string[];
} & {
    [Property in keyof Pick<
        IInteractionsGetQueryParameters,
        | "agentId"
        | "anomalyId"
        | "endDateTime"
        | "hasFrustration"
        | "hasGratitude"
        | "hasPraise"
        | "hasConfusion"
        | "hasEmpathy"
        | "highlightMaximumWords"
        | "highlightMinimumWords"
        | "isAgentSearch"
        | "isBotSearch"
        | "isCustomerSearch"
        | "isMetadataSearch"
        | "isSystemSearch"
        | "languageIsoCode"
        | "search"
        | "startDateTime"
        | "tagId"
        | "agentTagId"
        | "textSearchType"
    > as `interaction${Capitalize<Property>}`]?: IInteractionsGetQueryParameters[Property];
} & IInteractionsMaximumMetricParameters &
    IInteractionsMinimumMetricParameters;

export interface IInteractionVariable {
    readonly interactionVariableId: string;
    readonly interactionVariable: string;
    readonly value: string;
}

export interface IInteraction extends IBackendModel, IInteractionExperienceRatings {
    readonly agent: Pick<IAgent, "agentId" | "name"> | null;
    readonly agents?: IInteractionAgent[];
    readonly contentCleaned: string;
    readonly enterQueueDateTime: Date | null;
    readonly exitQueueDateTime: Date | null;
    readonly handleDateTime: Date | null;
    readonly hasFrustration: boolean;
    readonly hasPraise: boolean;
    readonly hasGratitude: boolean;
    readonly hasConfusion: boolean;
    readonly hasEmpathy: boolean;
    readonly interactionId: string;
    readonly interactionState: InteractionState;
    readonly interactionType: InteractionType;
    readonly preview: string[] | null;
    readonly searchMatch: string | null;
    readonly startDateTime: Date;
    readonly subject: string | null;
    readonly summary: string | null;
    readonly metrics: IInteractionMetrics;
    readonly url: string;
    readonly contactId: string | null;
    readonly languageIsoCode: string;
    readonly clientId: string | null;
    readonly reviewResult: InteractionTableReviewStatus | null;
    readonly resolutionStatus: ResolutionStatus | null;
}

export interface IInteractionsCount<Group> {
    readonly group: Group;
    readonly count: number | null;
    readonly proportion?: number;
    readonly dateTime?: Date | string;
}

export interface IInteractionTagsRequest {
    readonly items: IDynamicKeyField<string>;
}

export type IInteractionVariablesValues = IItem<IInteractionVariable>;

export type IInteractionsGetResponse = IPaginated<IInteraction>;

export type IInteractionsGetCsvResponse = string;

export type IInteractionsCountResponse<Group> = IItem<IInteractionsCount<Group>>;

export type IInteractionTagsResponse = IItem<IDynamicKeyField<string>>;

export type IInteractionVariablesGetResponse = IInteractionVariablesValues;

export type IInteractionMetricsAvailableGetQueryParameters = {
    readonly interactionType?: InteractionType[];
};

export type IInteractionMetricsAvailable = {
    readonly [Property in InteractionMetric]: boolean;
};

export interface IInteractionSummaryResponse {
    readonly summary: string[];
}

export interface IInteractionAgentPerformanceInteractionSummaries {
    description: string;
    examples: Array<{ interactionMessageId: string }>;
    title?: string;
}

export interface IInteractionAgentPerformanceSummariesResponse {
    summary: IInteractionAgentPerformanceInteractionSummaries[];
}

export enum ReviewStatus {
    ALL_REVIEWED = "all_reviewed",
    NOT_REVIEWED = "not_reviewed",
    PASSED = "passed",
    FAILED = "failed",
}

export enum InteractionTableReviewStatus {
    PASSED = "passed",
    FAILED = "failed",
    MIXED = "mixed",
}

export enum ResolutionStatus {
    Resolved = "RESOLVED",
    NotResolved = "UNRESOLVED",
    NotNeeded = "NO_RESOLUTION_NEEDED",
}

export enum ExperienceDriverRating {
    Negative = "NEGATIVE",
    Neutral = "NEUTRAL",
    Positive = "POSITIVE",
}

/** Identifies which AI-generated rationale a row in `interaction_rationales` holds. */
export enum RationaleType {
    ResolutionStatus = 1,
    AiContactReason = 2,
    BotExperienceDrivers = 3,
    AgentExperienceDrivers = 4,
    ProductExperienceDrivers = 5,
}

export interface IInteractionRationale {
    readonly interactionRationaleId: string;
    readonly interactionId: string;
    readonly rationaleTypeId: RationaleType;
    readonly rationaleType: string;
    readonly rationale: string;
    readonly interactionStartDateTime: Date;
}
